/**
 * Estado do módulo das estações meteorológicas (Zustand). Registra o codec de
 * URL `w` (de *weather*: `t` já é o tempo do loop) no import — `ui/modules.tsx`
 * importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { Dados, Estilo, Situacao, Variavel } from './types';

export const DEFAULT_ESTILO: Estilo = {
  // Temperatura é o que se procura primeiro num mapa de estações; as outras
  // estão a um toque no painel.
  variavel: 't',
  // 7 px de lado: medido sobre o enquadramento inicial (a Amazônia Legal
  // inteira), o menor em que o quadrado e o losango ainda se distinguem.
  tamanho: 7,
  opacidade: 1,
  mostrarVento: true,
  mostrarMetar: true,
};

export const LIMITES = {
  tamanho: { min: 4, max: 14, step: 0.5 },
  opacidade: { min: 0.2, max: 1, step: 0.05 },
} as const;

/** A camada começa desligada: é dado extra, não o assunto principal do app. */
export const DEFAULT_VISIVEL = false;

/** Letra de cada variável na URL. */
const LETRA: Readonly<Record<Variavel, string>> = { t: 't', ur: 'u', vento: 'v', p: 'p', chuva: 'c', td: 'd' };
const DA_LETRA = Object.fromEntries(Object.entries(LETRA).map(([v, l]) => [l, v])) as Record<string, Variavel>;

const DADOS_VAZIOS: Dados = { estacoes: [], t0: 0, passoMin: 60, slots: 48, gerado: null, maisNova: null };

export interface MeteoState {
  visivel: boolean;
  estilo: Estilo;
  situacao: Situacao;
  dados: Dados;

  setVisivel: (on: boolean) => void;
  setEstilo: (patch: Partial<Estilo>) => void;
  setSituacao: (s: Situacao) => void;
  setDados: (d: Dados) => void;
}

export const useMeteoStore = create<MeteoState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  estilo: DEFAULT_ESTILO,
  situacao: { estado: 'idle', erro: null },
  dados: DADOS_VAZIOS,

  setVisivel: (on) => set({ visivel: on }),
  setEstilo: (patch) => set((s) => ({ estilo: { ...s.estilo, ...patch } })),
  setSituacao: (situacao) => set({ situacao }),
  setDados: (dados) => set({ dados }),
}));

/**
 * `w=1` liga a camada; o resto guarda só o que difere do padrão:
 * `x<letra>` a variável (`u` umidade, `v` vento, `p` pressão, `c` chuva,
 * `d` orvalho), `t` tamanho, `o` opacidade, `s` inverte a seta do vento e
 * `a` inverte os aeródromos. Ex.: `w=1xut9a`.
 *
 * Como nos outros módulos, as letras booleanas significam "o contrário do
 * padrão", nunca um valor fixo — o link continua curto e certo se o padrão
 * mudar.
 */
export function encodeMeteo(s: Pick<MeteoState, 'visivel' | 'estilo'>): string | null {
  if (!s.visivel) return null;
  const e = s.estilo;
  let out = '1';
  if (e.variavel !== DEFAULT_ESTILO.variavel) out += `x${LETRA[e.variavel]}`;
  if (e.tamanho !== DEFAULT_ESTILO.tamanho) out += `t${e.tamanho}`;
  if (e.opacidade !== DEFAULT_ESTILO.opacidade) out += `o${e.opacidade}`;
  if (e.mostrarVento !== DEFAULT_ESTILO.mostrarVento) out += 's';
  if (e.mostrarMetar !== DEFAULT_ESTILO.mostrarMetar) out += 'a';
  return out;
}

export function decodeMeteo(value: string): Pick<MeteoState, 'visivel' | 'estilo'> | null {
  if (!value || value[0] !== '1') return null;
  const estilo: Estilo = { ...DEFAULT_ESTILO };
  // A variável sai do texto antes das outras letras: `xt`/`xp` não são tamanho nem nada.
  const resto = value.slice(1).replace(/x([a-z])/, (_, l: string) => {
    const v = DA_LETRA[l];
    if (v) estilo.variavel = v;
    return '';
  });

  const tamanho = /t(\d+(?:\.\d+)?)/.exec(resto);
  if (tamanho) estilo.tamanho = limitar(Number(tamanho[1]), LIMITES.tamanho.min, LIMITES.tamanho.max, DEFAULT_ESTILO.tamanho);
  const opac = /o(\d*\.?\d+)/.exec(resto);
  if (opac) estilo.opacidade = limitar(Number(opac[1]), LIMITES.opacidade.min, LIMITES.opacidade.max, DEFAULT_ESTILO.opacidade);
  if (resto.includes('s')) estilo.mostrarVento = !DEFAULT_ESTILO.mostrarVento;
  if (resto.includes('a')) estilo.mostrarMetar = !DEFAULT_ESTILO.mostrarMetar;
  return { visivel: true, estilo };
}

function limitar(n: number, min: number, max: number, padrao: number): number {
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : padrao;
}

registerUrlCodec({
  key: 'w',
  encode: () => encodeMeteo(useMeteoStore.getState()),
  decode: (value) => {
    const s = decodeMeteo(value);
    if (s) useMeteoStore.setState(s);
  },
  subscribe: (onChange) =>
    useMeteoStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.estilo !== b.estilo) onChange();
    }),
});
